/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Cell, Column } from "react-table";
import {
  AppAlert,
  BigText,
  DATE_NATIVE_FORMAT,
  formatAssignedUser,
  formatDate,
  formatValidDate,
  IPatientResponse,
  IStudyDetail,
  parseDateFromString,
  PERMISSIONS,
  SensitiveModal,
  storageClassesContainOnline,
  TableView as Table,
  ViewDiagnosticReportMode,
  ViewerLightIcon,
} from "@advapacs/common";
import { Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IRootState } from "@src/config/reducers";
import { useFlag } from "@unleash/proxy-client-react";
import { TabKey } from "./CriticalResultTabContainer";
import { PatientName } from "@src/components/Patient/PatientName";
import { dateFormatSelector } from "@src/selectors/common-selector";
import { ICriticalResult } from "@src/types/criticalResult";
import { hasPermission } from "@src/utils/utils";
import useOpenMeddream from "@src/hooks/viewers/useOpenMeddream";
import useOpenEUnity from "@src/hooks/viewers/useOpenEUnity";
import useOpenAdvapacsViewer from "@src/hooks/viewers/useOpenAdvapacsViewer";
import useTrackAdvapacsViewerLaunchClick from "@src/hooks/useTrackAdvapacsViewerLaunchClick";
import { useNavigate } from "react-router-dom";
import { isIOS } from "react-device-detect";
import ViewDiagnosticReportModal from "@src/pages/study/ViewDiagnosticReportModalNew";
import { useAppDispatch } from "@src/config/store";
import { getStudiesByCR } from "./ducks/operations";
import { DiagnosticAction } from "@src/reducers/slices/diagnosticReportSlice";
import { axiosInstanceRegion } from "@src/config/axios-region";
import { criticalResultApi } from "@src/config/api";
import { CriticalResultAction } from "@src/pages/criticalResult/ducks/slices";
import { StudyAction } from "@src/pages/study/ducks/studySlice";
import { usePatientVerificationModal } from "@src/hooks/usePatientVerificationModal";

interface CriticalResultTableProps {
  tabKey?: TabKey;
}

const CriticalResultTable: FC<CriticalResultTableProps> = ({
  tabKey = TabKey.MY_CASE,
}) => {
  const { t } = useTranslation("table");
  const enableSensitiveMFA = useFlag("sensitive-mfa");
  const enabledAdvapacsViewerInCp = useFlag("cp-advapacs-viewer");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const dateFormat = useSelector(dateFormatSelector);
  const openWithMeddream = useOpenMeddream();
  const openWithEUnity = useOpenEUnity();
  const openWithAdvapacsViewer = useOpenAdvapacsViewer();
  const trackAdvapacsViewerLaunchClick = useTrackAdvapacsViewerLaunchClick(
    "cp-critical-result-list"
  );
  const { loading, entities, studiesReport } = useSelector(
    (state: IRootState) => state.criticalResultReducer
  );
  const { account, features } = useSelector(
    (state: IRootState) => state.loginReducer
  );
  const { showPatientVerifySmsModal, phoneVerification } = useSelector(
    (state: IRootState) => state.studyReducer
  );

  const [selectedCriticalResult, setSelectedCriticalResult] =
    useState<ICriticalResult | null>(null);
  const [showViewReportModal, setShowViewReportModal] = useState(false);
  const [showSensitiveStudyModal, setShowSensitiveStudyModal] = useState(false);
  const [onCallbackPatientVerify, setOnCallbackPatientVerify] = useState(null);
  const [isSensitive, setIsSensitive] = useState(false);
  const [onConfirmSensitive, setOnConfirmSensitive] = useState(null);

  const [viewerErrorMessage, setViewerErrorMessage] = useState(null);

  const getTenantFeature = useCallback(
    (tenantId: string) => {
      return features.find((f) => f.clinicianPortal?.id === tenantId);
    },
    [features]
  );

  const getTenantPermissions = useCallback(
    (tenantId: string) => {
      return getTenantFeature(tenantId)?.permissions;
    },
    [getTenantFeature]
  );

  const hasTenantPermission = useCallback(
    (tenantId: string, can: string[]) => {
      return hasPermission(getTenantPermissions(tenantId), can);
    },
    [getTenantPermissions]
  );

  const verifyingPhoneNumberSegment = useMemo<{
    firstDigits: string;
    lastDigits: string;
  } | null>(() => {
    if (
      !phoneVerification ||
      !phoneVerification.studyIds ||
      phoneVerification.studyIds?.length === 0
    ) {
      return null;
    }
    let phonNumber;
    if (studiesReport?.length > 0) {
      // @ts-ignore
      const sensitiveStudies = studiesReport.filter((s) => s.sensitive);
      if (sensitiveStudies.length > 0) {
        // @ts-ignore
        phonNumber = sensitiveStudies[0]?.patient?.mobilePhone;
        dispatch(StudyAction.updateStudyVerifying(sensitiveStudies[0]));
      }
    }
    return phonNumber
      ? {
          firstDigits: phonNumber?.substring(0, 3),
          lastDigits: phonNumber?.substring(phonNumber.length - 3),
        }
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneVerification?.studyIds]);

  const setShowPatientVerifySmsModal = (value: boolean) => {
    {
      dispatch(
        StudyAction.setShowPatientVerifySmsModal({
          show: value,
          studyIds: [],
        })
      );
      if (!value) {
        dispatch(StudyAction.resetPhoneVerification());
      }
    }
  };

  const { modalRenderer } = usePatientVerificationModal({
    phoneVerification,
    verifyingPhoneNumberSegment,
    // @ts-ignore
    show: showPatientVerifySmsModal && enableSensitiveMFA,
    setShow: setShowPatientVerifySmsModal,
    // @ts-ignore
    onCallbackPatientVerify,
  });

  useEffect(() => {
    if (!showSensitiveStudyModal) setIsSensitive(false);
  }, [showSensitiveStudyModal]);

  useEffect(() => {
    if (phoneVerification?.verifyCodeSuccessful) {
      setShowPatientVerifySmsModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneVerification?.verifyCodeSuccessful]);

  const fetchStudies = useCallback(
    async (id: string) => {
      try {
        const params = {
          isMyCase: TabKey.MY_CASE === tabKey,
        };
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/return-await
        return await axiosInstanceRegion.get(
          criticalResultApi.GET_CRITICAL_RESULT_STUDIES(id),
          { params }
        );
      } catch (err) {
        setViewerErrorMessage(t("critical_result.critical_viewer_message"));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabKey]
  );

  const allowStudyIds = (
    items: IStudyDetail[],
    permission: string,
    isDetails = false,
    type?: string
  ) => {
    const allowStudies: IStudyDetail[] = [];

    features.forEach((f) => {
      items.forEach((s) => {
        if (
          s?.clinicianPortal?.id === f?.clinicianPortal?.id &&
          hasPermission(f?.permissions, [permission]) &&
          storageClassesContainOnline(s?.storageClasses) &&
          // @ts-ignore
          (f[type] || type === undefined)
        ) {
          allowStudies.push(s);
        }
      });
    });
    return isDetails ? allowStudies : allowStudies.map((a) => a.id);
  };

  const hasSensitiveStudyRequiredPatientVerifyPermission = (
    studyDetails: IStudyDetail[]
  ) => {
    return studyDetails.some((study) => {
      return hasTenantPermission(study.clinicianPortal?.id as string, [
        PERMISSIONS.VIEW_SENSITIVE_STUDY_PATIENT_VERIFY,
      ]);
    });
  };

  const handleMeddreamViewStudies = useCallback(
    async (studies: IStudyDetail[]) => {
      if (!studies || studies?.length === 0) return;
      try {
        const studyIds = allowStudyIds(
          studies,
          PERMISSIONS.ACCESS_MEDDREAM,
          false,
          "medDream"
        );
        const hasSensitiveStudyPatientVerify =
          hasSensitiveStudyRequiredPatientVerifyPermission(studies);
        if (hasSensitiveStudyPatientVerify) {
          setOnCallbackPatientVerify(
            // @ts-ignore
            () => () => openWithMeddream(studyIds as string[])
          );
        }
        if (
          studies.some((s) => s.sensitive) &&
          !hasSensitiveStudyPatientVerify
        ) {
          setShowSensitiveStudyModal(true);
          setIsSensitive(true);
          setOnConfirmSensitive(
            // @ts-ignore
            () => () => openWithMeddream(studyIds as string[])
          );
        } else {
          await openWithMeddream(studyIds as string[]);
        }
      } catch (err) {}
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [features]
  );

  const handleEUnityViewStudies = useCallback(
    async (studies?: IStudyDetail[]) => {
      if (!studies || studies?.length === 0) return;
      try {
        const patientsIds = Array.from(
          new Set(
            allowStudyIds(
              studies,
              PERMISSIONS.ACCESS_EUNITY,
              true,
              "eUnity"
              // @ts-ignore
            ).map((item) => item?.patient?.id)
          )
        );
        if (patientsIds?.length > 1) {
          // with critical results always only one patient
          return;
        } else {
          const allowStudies = allowStudyIds(
            studies,
            PERMISSIONS.ACCESS_EUNITY,
            true,
            "eUnity"
          ) as IStudyDetail[];
          const hasSensitiveStudyPatientVerify =
            hasSensitiveStudyRequiredPatientVerifyPermission(studies);
          if (hasSensitiveStudyPatientVerify) {
            setOnCallbackPatientVerify(
              // @ts-ignore
              () => () => openWithEUnity(allowStudies.map((a) => a.id))
            );
          }
          if (
            studies.some((s) => s.sensitive) &&
            !hasSensitiveStudyPatientVerify
          ) {
            setShowSensitiveStudyModal(true);
            setIsSensitive(true);
            setOnConfirmSensitive(
              // @ts-ignore
              () => () => openWithEUnity(allowStudies.map((a) => a.id))
            );
          } else {
            openWithEUnity(allowStudies.map((a) => a.id));
          }
        }
      } catch (e) {}
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [features]
  );

  const handleAdvapacsViewerViewStudies = useCallback(
    async (studies: IStudyDetail[]) => {
      if (!studies || studies?.length === 0) return;
      try {
        const studyIds = allowStudyIds(studies, PERMISSIONS.VIEW_STUDY);
        const hasSensitiveStudyPatientVerify =
          hasSensitiveStudyRequiredPatientVerifyPermission(studies);
        if (hasSensitiveStudyPatientVerify) {
          setOnCallbackPatientVerify(
            // @ts-ignore
            () => () => openWithAdvapacsViewer(studyIds as string[])
          );
        }
        if (
          studies.some((s) => s.sensitive) &&
          !hasSensitiveStudyPatientVerify
        ) {
          setShowSensitiveStudyModal(true);
          setIsSensitive(true);
          setOnConfirmSensitive(
            // @ts-ignore
            () => () => openWithAdvapacsViewer(studyIds as string[])
          );
        } else {
          await openWithAdvapacsViewer(studyIds as string[]);
        }
      } catch (err) {}
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [features]
  );

  // Resolves the viewer to open on devices where AdvaView is unsupported (iOS).
  // Meddream takes priority over EUnity when the tenant has both available.
  const fallbackViewer = (tenantId: string) => {
    const tenantFeature = getTenantFeature(tenantId);
    const canAccessMeddream =
      tenantFeature?.medDream &&
      hasTenantPermission(tenantId, [PERMISSIONS.ACCESS_MEDDREAM]);
    const canAccessEUnity =
      tenantFeature?.eUnity &&
      hasTenantPermission(tenantId, [PERMISSIONS.ACCESS_EUNITY]);

    if (canAccessMeddream) {
      return handleMeddreamViewStudies;
    }
    if (canAccessEUnity) {
      return handleEUnityViewStudies;
    }
    return undefined;
  };

  const handleClickAdvapacsViewer = useCallback(
    async (crId: string, tenantId?: string) => {
      try {
        const response = await fetchStudies(crId);
        const callbackOpenViewer = isIOS
          ? fallbackViewer(tenantId as string)
          : handleAdvapacsViewerViewStudies;
        if (callbackOpenViewer) {
          callbackOpenViewer(response?.data?.content);
        } else {
          setViewerErrorMessage(t("common:error_messages.viewer_unavailable"));
        }
      } catch (err) {
        setViewerErrorMessage(t("critical_result.critical_viewer_message"));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchStudies, handleAdvapacsViewerViewStudies, t]
  );

  const columns = useMemo(
    () => {
      const tmp: Column[] | unknown[] = [
        {
          id: "createdBy",
          Header: `${t("created_by")}`,
          Cell: ({ row }: Cell<ICriticalResult>) => {
            return (
              <BigText text={formatAssignedUser(row.original?.createdBy)} />
            );
          },
          show: true,
          disableSortBy: true,
        },
        {
          id: "createdAt",
          Header: `${t("created_at")}`,
          Cell: ({ row }: Cell<ICriticalResult>) => {
            return (
              <BigText
                text={formatValidDate(
                  row.original?.createdAt,
                  account?.localisation?.dateTimeFormat
                )}
                maxWidth={"100%"}
              />
            );
          },
          show: true,
          disableSortBy: true,
        },
        {
          id: "patientName",
          Header: `${t("patient_name")}`,
          accessor: `${t("patient_name")}`,
          Cell: ({ row }: Cell<ICriticalResult>) => {
            const patient = row.original?.patient;
            return <PatientName patient={patient as IPatientResponse} />;
          },
          show: true,
          disableSortBy: true,
          width: 120,
        },
        {
          Header: `${t("patient_id")}`,
          Cell: ({ row }: Cell<ICriticalResult>) => {
            const patient = row.original?.patient;
            return (
              <span className="pid-no-wrap">
                {patient?.patientIds
                  ?.map((patientId) => patientId.pid)
                  .join(", ")}
              </span>
            );
          },
        },
        {
          Header: `${t("dob")}`,
          Cell: ({ row }: Cell<ICriticalResult>) => {
            const patient = row.original?.patient;
            const dobFormat = patient?.dob
              ? formatDate(
                  parseDateFromString(patient?.dob, DATE_NATIVE_FORMAT),
                  dateFormat
                )
              : null;

            return <span>{dobFormat}</span>;
          },
          width: 90,
        },
        {
          Header: `${t("description")}`,
          Cell: ({ row }: Cell<ICriticalResult>) => {
            const cr = row.original;
            return (
              <span title={cr?.description} className="no-wrap">
                {cr.description}{" "}
              </span>
            );
          },
          width: 200,
        },
        {
          id: "status",
          Header: `${t("status")}`,
          accessor: "status ",
          width: 120,
          show: true,
          Cell: ({ row }: Cell<ICriticalResult>) => {
            const { viewedAt, acknowledgedAt } = row.original;
            if (acknowledgedAt) {
              return (
                <Badge bg="success" className="text-white">
                  {t("acknowledged")}
                </Badge>
              );
            }
            if (viewedAt) {
              return (
                <Badge bg="warning" className="text-white">
                  {t("viewed")}
                </Badge>
              );
            }
            return (
              <Badge bg="danger" className="text-white">
                {t("order.new")}
              </Badge>
            );
          },
        },
        {
          id: "customers",
          Header: `${t("source")}`,
          Cell: ({ row }: Cell<ICriticalResult>) => {
            const cr = row.original;
            return <span>{cr?.clinicianPortal?.name}</span>;
          },
          width: 180,
          show: true,
        },
        {
          id: "action",
          show: true,
          width: 130,
          Cell: ({ row }: Cell<ICriticalResult>) => {
            const cr = row.original;
            const tenantFeature = getTenantFeature(
              cr.clinicianPortal?.id as string
            );
            // The UI will handle this case itself. If the account is AdvaPACS View-only, the UI will hide EUnity and MedDream.
            const showEUnity =
              (tenantFeature?.eUnity && !account?.advaViewOnly) || false;
            const showMeddream =
              (tenantFeature?.medDream && !account?.advaViewOnly) || false;

            return (
              <>
                <div className="list-icons text-right" data-action="action">
                  <>
                    {cr.hasReport &&
                      hasTenantPermission(cr?.clinicianPortal?.id as string, [
                        PERMISSIONS.VIEW_REPORT,
                      ]) && (
                        <span
                          data-action="action"
                          aria-hidden
                          className="list-icons-item cursor-pointer"
                          style={{ fontSize: "1.5em" }}
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedCriticalResult(row.original);
                            dispatch(
                              getStudiesByCR(
                                row?.original?.id as string,
                                TabKey.MY_CASE === tabKey
                              )
                            );
                            setShowViewReportModal(true);
                          }}
                        >
                          <i
                            className="icon-file-text2"
                            data-action="action"
                            title={t("view_diagnostic_report")}
                          />
                        </span>
                      )}
                    {showEUnity &&
                      hasTenantPermission(cr?.clinicianPortal?.id as string, [
                        PERMISSIONS.ACCESS_EUNITY,
                      ]) && (
                        <span
                          data-action="action"
                          aria-hidden
                          className="list-icons-item cursor-pointer"
                          style={{ fontSize: "1.5em" }}
                          onClick={async () => {
                            try {
                              trackAdvapacsViewerLaunchClick(
                                "open-viewer",
                                "eunity"
                              );
                              const response = await fetchStudies(
                                row?.original?.id as string
                              );
                              handleEUnityViewStudies(response?.data?.content);
                            } catch (err) {
                              setViewerErrorMessage(
                                t("critical_result.critical_viewer_message")
                              );
                            }
                          }}
                        >
                          <i
                            className="icon-Eunity"
                            data-action="action"
                            title={t("button.eunity")}
                          />
                        </span>
                      )}
                    {showMeddream &&
                      hasTenantPermission(cr?.clinicianPortal?.id as string, [
                        PERMISSIONS.ACCESS_MEDDREAM,
                      ]) && (
                        <span
                          data-action="action"
                          aria-hidden
                          className="list-icons-item cursor-pointer text-danger"
                          style={{ fontSize: "1.5em" }}
                          onClick={async () => {
                            try {
                              trackAdvapacsViewerLaunchClick(
                                "open-viewer",
                                "meddream"
                              );
                              const response = await fetchStudies(
                                row?.original?.id as string
                              );
                              handleMeddreamViewStudies(
                                response?.data?.content
                              );
                            } catch (err) {
                              setViewerErrorMessage(
                                t("critical_result.critical_viewer_message")
                              );
                            }
                          }}
                        >
                          <i
                            className="icon-Meddream"
                            data-action="action"
                            title={t("button.meddream")}
                          />
                        </span>
                      )}
                    {enabledAdvapacsViewerInCp &&
                      hasTenantPermission(cr?.clinicianPortal?.id as string, [
                        PERMISSIONS.VIEW_STUDY,
                      ]) && (
                        <span
                          data-action="action"
                          aria-hidden
                          className="list-icons-item cursor-pointer"
                          style={{ fontSize: "1.5em" }}
                          onClick={() => {
                            trackAdvapacsViewerLaunchClick("open-viewer");
                            handleClickAdvapacsViewer(
                              row?.original?.id as string,
                              cr?.clinicianPortal?.id
                            );
                          }}
                        >
                          <img
                            className="advaview-icon"
                            data-action="action"
                            title={t("button.advapacs_viewer")}
                            src={ViewerLightIcon}
                            alt="AdvaView"
                          />
                        </span>
                      )}
                  </>
                </div>
              </>
            );
          },
        },
      ];
      return tmp;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, tabKey, features, enabledAdvapacsViewerInCp, account]
  );

  return (
    <>
      {viewerErrorMessage && (
        <AppAlert.ErrorAlert
          error={viewerErrorMessage}
          dismissible
          onClose={() => {
            setViewerErrorMessage(null);
          }}
        />
      )}
      {showViewReportModal && studiesReport?.length > 0 && (
        <ViewDiagnosticReportModal
          portalId={selectedCriticalResult?.clinicianPortal?.id as string}
          patientDetail={selectedCriticalResult?.patient as IPatientResponse}
          studies={studiesReport as IStudyDetail[]}
          viewMode={ViewDiagnosticReportMode.ORDER}
          viewModeId={selectedCriticalResult?.linkedOrder?.id}
          accessionNumber={selectedCriticalResult?.linkedOrder?.accessionNumber}
          studyDate={selectedCriticalResult?.linkedOrder?.occurrenceAt}
          // @ts-ignore
          modalities={[selectedCriticalResult?.linkedOrder?.modality]}
          // @ts-ignore
          isSensitive={studiesReport?.some((s) => s.sensitive === true)}
          show={showViewReportModal}
          hasReport={selectedCriticalResult?.hasReport}
          onClose={() => {
            setSelectedCriticalResult(null);
            setShowViewReportModal(false);
            dispatch(DiagnosticAction.clearData());
            dispatch(CriticalResultAction.getStudyReportFail());
          }}
        />
      )}
      {showPatientVerifySmsModal && enableSensitiveMFA && modalRenderer}
      {showSensitiveStudyModal && (
        <SensitiveModal
          show={showSensitiveStudyModal}
          onClose={() => {
            setShowSensitiveStudyModal(false);
          }}
          onConfirm={async () => {
            // @ts-ignore
            if (onConfirmSensitive) await onConfirmSensitive();
            setShowSensitiveStudyModal(false);
          }}
          sensitive={isSensitive}
          can={[PERMISSIONS.VIEW_SENSITIVE_STUDY]}
          // @ts-ignore
          permissions={features.map((x) => x.permissions).flat(2)}
          header={t("sensitive_study")}
        />
      )}
      <Table
        data={entities || []}
        columns={columns}
        loading={loading}
        noDataMessage={t("no_results")}
        isDragNDrop={false}
        enabledClickToView={true}
        onClickToView={(row) => {
          setSelectedCriticalResult(row.original);
          navigate(`/CriticalResults/${row.original.id}?tabKey=${tabKey}`);
        }}
      />
    </>
  );
};

// @ts-ignore
export default CriticalResultTable;
